import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../../contexts/AuthContext';
import { PLATFORM_SETTINGS_QUERY, PROJECT_TRANSFER_INFO_QUERY } from '../../graphql/queries';
import { ACCEPT_PROJECT_TRANSFER_MUTATION } from '../../graphql/mutations';
import BrandMark from '../../components/common/BrandMark';
import styles from './TransferAccept.module.css';

interface TransferSection {
  type: string;
  label: string;
  count: number;
  size: number;
}

const formatSize = (bytes: number): string => {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const TransferAccept = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated: hasToken, loading: authLoading } = useAuth();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const { data, loading, error } = useQuery(PROJECT_TRANSFER_INFO_QUERY, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });
  const { data: settingsData, loading: settingsLoading } = useQuery(PLATFORM_SETTINGS_QUERY, { fetchPolicy: 'cache-first' });
  const registrationEnabled = settingsData?.platformSettings?.usersAllowedToRegister ?? true;

  const [acceptTransfer, { loading: accepting }] = useMutation(ACCEPT_PROJECT_TRANSFER_MUTATION, {
    onCompleted: (res) => navigate(`/projects/${res.acceptProjectTransfer.id}`),
    onError: (err) => setAcceptError(err.message),
  });

  const info = data?.projectTransferInfo;
  const isAuthenticated = hasToken && !!user;
  const isRecipient = isAuthenticated && !!user && !!info && user.email.toLowerCase() === info.toEmail;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <BrandMark size={28} variant="tileWhite" decorative />
          <span className={styles.brand}>Infrio</span>
        </div>

        {loading || authLoading || settingsLoading ? (
          <div className={styles.body}>
            <p className={styles.muted}>Loading transfer…</p>
          </div>
        ) : error || !info ? (
          <div className={styles.body}>
            <h1 className={styles.title}>Transfer not found</h1>
            <p className={styles.muted}>This transfer link is invalid, or the project no longer exists.</p>
          </div>
        ) : info.status === 'accepted' ? (
          <div className={styles.body}>
            <h1 className={styles.title}>Transfer completed</h1>
            <p className={styles.muted}>
              Ownership of <strong>{info.projectName}</strong> has been transferred to {info.toEmail}.
            </p>
            {isRecipient && (
              <button className={styles.acceptBtn} onClick={() => navigate(`/projects/${info.projectId}`)}>
                Open project
              </button>
            )}
          </div>
        ) : info.status !== 'pending' ? (
          <div className={styles.body}>
            <h1 className={styles.title}>Transfer no longer active</h1>
            <p className={styles.muted}>This transfer was cancelled by the project owner.</p>
          </div>
        ) : (
          <div className={styles.body}>
            <h1 className={styles.title}>Project transfer</h1>
            <p className={styles.lead}>
              <strong>{info.fromName}</strong> wants to transfer ownership of{' '}
              <strong>{info.projectName}</strong> to <strong>{info.toEmail}</strong>.
              Accepting moves the project and all of its files to your account —
              its storage counts against your account's limit.
            </p>
            {info.projectDescription && <p className={styles.muted}>{info.projectDescription}</p>}

            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Section</th>
                  <th className={styles.num}>Files</th>
                  <th className={styles.num}>Size</th>
                </tr>
              </thead>
              <tbody>
                {(info.sections as TransferSection[]).map((s) => (
                  <tr key={s.type}>
                    <td>{s.label}</td>
                    <td className={styles.num}>{s.count}</td>
                    <td className={styles.num}>{formatSize(s.size)}</td>
                  </tr>
                ))}
                <tr className={styles.totalRow}>
                  <td>Total</td>
                  <td className={styles.num}>{info.fileCount}</td>
                  <td className={styles.num}>{formatSize(info.storageUsed)}</td>
                </tr>
              </tbody>
            </table>

            {acceptError && <div className={styles.errorBox}>{acceptError}</div>}

            {isRecipient ? (
              <button className={styles.acceptBtn} disabled={accepting} onClick={() => acceptTransfer({ variables: { token } })}>
                {accepting ? 'Accepting…' : 'Accept transfer'}
              </button>
            ) : info.recipientExists ? (
              isAuthenticated ? (
                <div className={styles.warnBox}>
                  This transfer was sent to <strong>{info.toEmail}</strong>, but you are signed in
                  as <strong>{user?.email}</strong>. Sign in with the recipient account to accept it.
                </div>
              ) : (
                <button
                  className={styles.acceptBtn}
                  onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/transfer/${token}`)}`)}
                >
                  Sign in to accept
                </button>
              )
            ) : !registrationEnabled ? (
              <div className={styles.warnBox}>
                This transfer was sent to <strong>{info.toEmail}</strong>, which has no Infrio account yet,
                and new accounts cannot be created right now. Ask the person who sent it or an Infrio
                administrator to set up that account, then open this link again.
              </div>
            ) : isAuthenticated ? (
              <div className={styles.warnBox}>
                This transfer was sent to <strong>{info.toEmail}</strong>, which has no Infrio account yet.
                Sign out, then open this link again to create that account.
              </div>
            ) : (
              <>
                <p className={styles.muted}>
                  There is no Infrio account for {info.toEmail} yet. Create one to accept the
                  transfer — the project will be waiting in your new account.
                </p>
                <button
                  className={styles.acceptBtn}
                  onClick={() => navigate(`/register?transfer=${token}&email=${encodeURIComponent(info.toEmail)}`)}
                >
                  Create account to accept
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransferAccept;
